import { EntitlementService } from './entitlement.service';
import { HeaderService } from './header.service';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { User } from '../User';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';

const NAVY_USER_SESSION_NAME = 'navy_user';
const LOG_VAR = 'logVar';
const TIMEOUT_VAR = 'timeoutHook';
const TIME_OUT_INTERVAL = 20 * 60 * 1000; // 20 minutes

const EXPIRING_VAR = 'expiringVar';
const EXPIRING_INTERVAL = 15 * 60 * 1000; // 15 minutes

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user: User;
  token: string;
  loggedIn = false;
  redirectUrl = '';
  authenticated = new BehaviorSubject<boolean>(null);

  @Output()
  loginStatusChanged: EventEmitter<boolean> = new EventEmitter();

  @Output()
  sessionExpiredEvent: EventEmitter<{
    expired: boolean;
    timeoutId: number;
  }> = new EventEmitter();

  @Output()
  sessionExpiringEvent: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private http: HttpClient,
    private headerService: HeaderService,
    private toastrService: ToastrService,
    private entitlementService: EntitlementService,
    private router: Router
  ) {}

  resetActivityTimeouts() {
    this.clearActiveTimeouts();
    const activityTimeout = window.setTimeout(() => {
      this.sessionExpiredEvent.emit({
        expired: true,
        timeoutId: activityTimeout
      });
    }, TIME_OUT_INTERVAL);

    sessionStorage.setItem(LOG_VAR, activityTimeout.toString());

    const expiringTimeout = window.setTimeout(() => {
      this.sessionExpiringEvent.emit(true);
    }, EXPIRING_INTERVAL);

    sessionStorage.setItem(EXPIRING_VAR, expiringTimeout.toString());
  }

  clearExpiringTimeout() {
    const expiringTimeout = Number(sessionStorage.getItem(EXPIRING_VAR) || 0);
    window.clearTimeout(expiringTimeout);
  }

  clearActiveTimeouts() {
    // Save and restore the timer hook in case the class is garbage collected
    const activityTimeout = Number(sessionStorage.getItem(LOG_VAR) || 0);
    window.clearTimeout(activityTimeout);
    this.clearExpiringTimeout();
  }

  updateLastActivity() {
    const now = new Date().getTime();
    const lastActivity = Number(sessionStorage.getItem(TIMEOUT_VAR) || now);

    if (this.loggedIn && now - TIME_OUT_INTERVAL < lastActivity) {
      sessionStorage.setItem(TIMEOUT_VAR, now.toString());
      this.resetActivityTimeouts();
      return true;
    }
    sessionStorage.removeItem(TIMEOUT_VAR);
    this.clearActiveTimeouts();
    return false;
  }
  preAuthenticate() {
    const url = `${environment.rootUrl}/login/preAuthenticate`;
    return this.http.get(url, this.headerService.httpOptionsJson);
  }

  authenticate() {
    // This is the form our spring boot server wants the data in:
    const url = `${environment.rootUrl}/login/authenticate`;
    const httpObs = this.http.get<User>(
      url,
      this.headerService.getHttpOptionsXAuthHeaderFromCookie()
    );

    return Observable.create((observer) => {
      httpObs.subscribe(
        (userDto) => {
          const item = sessionStorage.getItem('registration');
          if ('true' === userDto.requiresNavyCashEnrollment) {
            this.user = userDto;
            if (item === 'cardholder') {
              this.router.navigate(['/selfEnroll/getStarted']);
            } else if (item === 'agent') {
              if (userDto.hasMultipleAgentRoles) {
                this.setUser(userDto);
                this.router.navigate(['/users/messages']);
              }
              if (userDto.role === 'HOLD') {
                sessionStorage.setItem('isHoldRoleAgent', 'true');
                this.setUser(userDto);
                this.router.navigate(['/users/messages']);
              } else {
                console.log('Error: Agent was not Auto enrolled.');
              }
            }
          } else {
            if (
              item === 'cardholder' &&
              'true' === userDto.hasSevenSeriesCard
            ) {
              // remove entitlements to disable chardholder tab
              userDto.entitlements = null;
            }
            this.setUser(userDto);
            window.sessionStorage.token = userDto.token;
            this.resetActivityTimeouts();
            this.loginStatusChanged.emit(true);
            observer.next(userDto);
          }
        },
        (errorResponse: any) => {
          this.toastrService.error(errorResponse.error);
        }
      );
    });
  }

  getUser() {
    return this.user ? this.user : new User();
  }

  resetLoggedInUserFromSession() {
    const sessionUser = window.sessionStorage.getItem(NAVY_USER_SESSION_NAME);
    if (sessionUser) {
      this.setUser(<User>JSON.parse(sessionUser));
    }
  }

  setUser(authenticationResult: any): any {
    this.user = authenticationResult;
    this.token = authenticationResult.token;
    this.entitlementService.setEntitlements(this.user.entitlements);
    window.sessionStorage[NAVY_USER_SESSION_NAME] =
      JSON.stringify(authenticationResult);
    this.loggedIn = true;
    this.authenticated.next(true);
  }

  logout() {
    this.user = null;
    this.token = null;
    this.loggedIn = false;
    this.entitlementService.setEntitlements([]);
    this.clearActiveTimeouts();
    this.clearSessionVariables();
    this.loginStatusChanged.emit(false);
    this.authenticated.next(false);
  }

  clearSessionVariables() {
    window.sessionStorage.removeItem(NAVY_USER_SESSION_NAME);
    window.sessionStorage.removeItem('token');
    window.sessionStorage.removeItem(LOG_VAR);
    window.sessionStorage.removeItem(TIMEOUT_VAR);
    window.sessionStorage.removeItem(EXPIRING_VAR);
    window.sessionStorage.removeItem('isHoldRoleAgent');
  }

  isLoggedIn() {
    return this.loggedIn;
  }

  clearCookieFlag() {
    const sessionUser = this.getSessionUser();
    this.setUser(sessionUser);
  }

  clearPasswordChangeFlag() {
    const sessionUser = this.getSessionUser();
    this.setUser(sessionUser);
  }

  clearSecSetupFlag() {
    const sessionUser = this.getSessionUser();
    this.setUser(sessionUser);
  }

  setToken(token: string) {
    const sessionUser = this.getSessionUser();
    sessionUser.token = token;
    window.sessionStorage.token = token;
    this.setUser(sessionUser);
  }

  getSessionUser() {
    const sessionUser = window.sessionStorage.getItem(NAVY_USER_SESSION_NAME);
    if (sessionUser) {
      return <User>JSON.parse(sessionUser);
    }
    return null;
  }
}
